import { FileText, X } from 'lucide-react';
import type { Attachment } from '@medicail/shared';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/uploads';
import { useAttachmentUrl } from './api';
import { Spinner } from '@/components/ui';

/** Attachment shown inside a sent message bubble. */
export function MessageAttachment({ attachment }: { attachment: Attachment }) {
  const url = useAttachmentUrl(attachment.attachmentId, attachment.kind === 'image');
  if (attachment.kind === 'image') {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open image ${attachment.fileName}`}
        className="block size-28 overflow-hidden rounded-2xl bg-white/15 ring-1 ring-white/25 sm:size-36"
      >
        {url ? (
          <img src={url} alt={attachment.fileName} className="size-full object-cover" />
        ) : (
          <span className="grid size-full place-items-center text-xs opacity-80">{attachment.fileName}</span>
        )}
      </a>
    );
  }
  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-xl bg-white/15 px-3 py-2 text-sm ring-1 ring-white/25">
      <FileText className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        <span className="block max-w-[12rem] truncate font-medium">{attachment.fileName}</span>
        <span className="block text-xs opacity-75">{formatBytes(attachment.sizeBytes)}</span>
      </span>
    </span>
  );
}

export interface ChipProps {
  fileName: string;
  sizeBytes: number;
  kind: 'image' | 'document';
  previewUrl?: string;
  status: 'uploading' | 'done' | 'error';
  progress: number;
  onRemove: () => void;
}

/** Attachment chip inside the composer: thumbnail, progress ring, remove button. */
export function ComposerChip({ fileName, sizeBytes, kind, previewUrl, status, progress, onRemove }: ChipProps) {
  const pct = Math.round(progress * 100);
  return (
    <li
      className={cn(
        'group relative flex max-w-full animate-pop-in items-center gap-2.5 rounded-2xl border bg-surface-muted py-1.5 pl-1.5 pr-9',
        status === 'error' ? 'border-danger-500' : 'border-line',
      )}
    >
      <span className="relative grid size-11 shrink-0 place-items-center overflow-hidden rounded-xl bg-surface text-ink-muted">
        {kind === 'image' && previewUrl ? (
          <img src={previewUrl} alt="" className="size-full object-cover" />
        ) : (
          <FileText className="size-5" aria-hidden="true" />
        )}
        {status === 'uploading' && (
          <span className="absolute inset-0 grid place-items-center bg-navy-900/55 text-white">
            <Spinner />
          </span>
        )}
      </span>
      <span className="min-w-0">
        <span className="block max-w-[10rem] truncate text-sm font-medium text-ink sm:max-w-[14rem]">{fileName}</span>
        <span className="block text-xs text-ink-muted" aria-live="polite">
          {status === 'uploading' ? `Uploading ${pct}%` : status === 'error' ? 'Upload failed' : formatBytes(sizeBytes)}
        </span>
      </span>
      {status === 'uploading' && (
        <span className="absolute inset-x-2 bottom-0 h-0.5 overflow-hidden rounded-full bg-line" aria-hidden="true">
          <span className="block h-full bg-action transition-[width]" style={{ width: `${pct}%` }} />
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${fileName}`}
        className="absolute right-1.5 top-1.5 grid size-6 place-items-center rounded-full text-ink-muted transition hover:bg-hover hover:text-ink"
      >
        <X className="size-3.5" />
      </button>
    </li>
  );
}
